import { useAuth } from "../../context/AuthContext";
import { CalendarRange } from "lucide-react";
import { formatAvailabilityDate } from "../../lib/availability";
import type { DataAvailability } from "./use-analytics";

interface Props {
  availability: DataAvailability | null | undefined;
  campaign?: string | null;
  totalCalls: number;
  onJumpToData?: (start: Date, end: Date) => void;
}

function parseBound(value: string | null | undefined): Date | null {
  if (!value) return null;
  const text = String(value).trim();
  if (!text) return null;
  const parsed = new Date(text.includes("T") ? text : text.replace(" ", "T"));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Tells the user WHERE a campaign's data actually lives.
 *
 * When "Last 7 Days" + campaign X returns zero rows, the charts alone look
 * broken — this banner says "campaign X has N calls from <start> to <end>"
 * and offers a one-click jump to that range.
 */
export function DataAvailabilityBanner({ availability, campaign, totalCalls, onJumpToData }: Props) {
  const { user } = useAuth() as any;
  const globalTz = user?.timezone;
  if (!availability) return null;

  const scopeMin = campaign ? availability.campaign_min : availability.filtered_min || availability.overall_min;
  const scopeMax = campaign ? availability.campaign_max : availability.filtered_max || availability.overall_max;
  const scopeCount = campaign ? availability.campaign_count : availability.filtered_count ?? availability.overall_count;

  const startLabel = formatAvailabilityDate(scopeMin, globalTz);
  const endLabel = formatAvailabilityDate(scopeMax, globalTz);
  if (!startLabel || !endLabel) return null;

  const empty = totalCalls === 0;
  const title = campaign
    ? empty
      ? `No "${campaign}" calls in the selected range`
      : `Campaign "${campaign}" data`
    : empty
      ? "No calls in the selected range"
      : "Available data";

  const detail = campaign
    ? `${scopeCount ?? 0} call${(scopeCount ?? 0) === 1 ? "" : "s"} available from ${startLabel} to ${endLabel}`
    : `Data available from ${startLabel} to ${endLabel}`;

  const start = parseBound(scopeMin);
  const end = parseBound(scopeMax);
  // Only offer the jump when the data actually sits outside what is shown
  // (or when the current view is empty) — otherwise the button is noise.
  const canJump = Boolean(onJumpToData && start && end && (empty || totalCalls < (scopeCount ?? 0)));

  return (
    <div
      className={
        empty
          ? "flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
          : "flex flex-wrap items-center gap-3 rounded-xl border border-line bg-soft px-4 py-2.5"
      }
    >
      <CalendarRange className={empty ? "h-4 w-4 shrink-0 text-amber-600" : "h-4 w-4 shrink-0 text-ink-3"} />
      <p className={empty ? "text-sm font-semibold text-amber-900" : "text-sm font-medium text-ink-2"}>
        {title}
        <span className={empty ? "ml-2 font-normal text-amber-800" : "ml-2 font-normal text-ink-3"}>
          {detail}
        </span>
      </p>
      {canJump && (
        <button
          onClick={() => onJumpToData!(start!, end!)}
          className="ml-auto rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-hover"
        >
          View {startLabel} – {endLabel}
        </button>
      )}
    </div>
  );
}
